import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SITE } from "../mock";
import { Search, Radio, ChevronDown, Menu, X, User, LogOut, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";

// Vexent Wire's own vocabulary — nothing lifted from any competitor.
const COVERAGE = [
  { label: "Markets",         href: "/section/markets" },
  { label: "Commodities",     href: "/section/commodities" },
  { label: "Precious Metals", href: "/section/precious-metals" },
  { label: "Macro",           href: "/section/macro" },
  { label: "Policy",          href: "/section/policy" },
  { label: "Geopolitics",     href: "/section/geopolitics" },
  { label: "Personal Finance",href: "/section/personal-finance" },
  { label: "Commentary",      href: "/section/commentary" },
  { label: "Strategy",        href: "/section/strategy" },
  { label: "Research",        href: "/section/research" },
];

const SYNDICATE = [
  { label: "The Wire",          href: "/wire" },
  { label: "Precious Wire",     href: "/wire?source=precious" },
  { label: "Macro Wire",        href: "/wire?source=macro" },
];

const MASTHEAD = [
  { label: "About Vexent Wire", href: "/masthead/about" },
  { label: "Editorial Policy",      href: "/masthead/editorial" },
  { label: "Ethics & Disclosure",   href: "/masthead/ethics" },
  { label: "Contact",               href: "/masthead/contact" },
  { label: "Privacy",               href: "/masthead/privacy" },
  { label: "Disclaimer",            href: "/masthead/disclaimer" },
];

const Header = () => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signIn, signOut } = useAuth();
  const [today, setToday] = useState(() =>
    new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  );

  useEffect(() => {
    const t = setInterval(() => {
      const nxt = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      setToday((prev) => (prev !== nxt ? nxt : prev));
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setSearchOpen(false);
  };

  const NavLink = ({ to, children, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className="font-condensed uppercase tracking-[0.08em] text-[13px] font-bold text-[#0B1F33] hover:text-[#0891B2] transition-colors"
    >
      {children}
    </Link>
  );

  const NavDropdown = ({ label, items, testid }) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="font-condensed uppercase tracking-[0.08em] text-[13px] font-bold text-[#0B1F33] hover:text-[#0891B2] inline-flex items-center gap-1 outline-none transition-colors"
        data-testid={testid}
      >
        {label}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="rounded-none border border-[#D7DEE5] bg-white p-1 shadow-lg min-w-[220px]"
      >
        {items.map((it) => (
          <DropdownMenuItem
            key={it.href}
            onSelect={() => navigate(it.href)}
            className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold text-[#0B1F33] hover:bg-[#0B1F33] hover:text-white cursor-pointer px-3 py-2"
          >
            {it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="bg-white border-b border-[#D7DEE5] sticky top-0 z-40">
      {/* Top utility strip */}
      <div className="border-b border-[#D7DEE5] bg-[#0B1F33]">
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between h-9 text-[11px] tracking-wider uppercase text-[#D7DEE5]">
          <div className="flex items-center gap-4">
            <span className="font-mono-tick">{today}</span>
            <span className="hidden md:inline text-[#D7DEE5]/40">|</span>
            <span className="hidden md:inline">Vol. XXV · Independent Research Since {SITE.founded}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline-flex items-center gap-1.5 text-[#0891B2]" aria-label="Live feed">
              <Radio className="w-3 h-3 animate-pulse" />
              <span className="text-[10px] tracking-[0.24em] font-bold">VexGlobalLinks · 40 Countries · Updated Every 5 Minutes</span>
            </span>
          </div>
        </div>
      </div>

      {/* Masthead / wordmark */}
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" data-testid="site-wordmark">
          <div className="flex flex-col items-start">
            <img
              src="/assets/vexent-logo.png"
              alt="Vexent Wire"
              className="h-[120px] md:h-[176px] w-auto object-contain"
              data-testid="site-logo"
            />
            <div className="mt-1.5 flex items-center gap-2 md:gap-3 whitespace-nowrap">
              <span className="h-px w-4 md:w-8 bg-[#B8E9F1]" />
              <span className="text-[12px] md:text-[14px] tracking-[0.32em] md:tracking-[0.42em] uppercase text-[#0891B2] font-medium antialiased subpixel-antialiased">Strategy · Research · Commentary</span>
              <span className="h-px w-4 md:w-8 bg-[#B8E9F1]" />
            </div>
          </div>
        </Link>

        {/* Desktop nav — institutional catalog only. Feed / Premium /
            Coverage / Syndicate / Desk / Archive / Masthead were dropped in
            the institutional pivot (news-site nav didn't fit a research firm). */}
        <nav className="hidden lg:flex items-center gap-8 ml-auto" data-testid="primary-nav">
          <NavLink to="/" testid="nav-reports">Reports</NavLink>
          <NavLink to="/podcast" testid="nav-podcast">Podcast</NavLink>
          <NavLink to="/masthead" testid="nav-masthead">About</NavLink>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="p-2 hover:bg-[#D7DEE5] rounded transition-colors"
            aria-label="Search"
            data-testid="search-toggle"
          >
            <Search className="w-4 h-4 text-[#0B1F33]" />
          </button>
          <Link
            to="/subscribe"
            data-testid="header-subscribe"
            className="hidden md:inline-flex items-center bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] font-condensed uppercase tracking-[0.14em] text-[12px] font-bold px-4 py-2 transition-colors"
          >
            Subscribe
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="header-account-menu"
                className="hidden md:inline-flex items-center gap-2 border border-[#D7DEE5] hover:border-[#B8E9F1] px-3 py-2 font-condensed uppercase tracking-[0.14em] text-[11px] font-bold text-[#0B1F33] transition-colors outline-none"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-3.5 h-3.5" />
                )}
                <span className="max-w-[110px] truncate">{user.name || user.email?.split("@")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-none border border-[#D7DEE5] bg-white p-1 min-w-[220px]"
              >
                <div className="px-3 py-2 text-[11px] text-neutral-600 border-b border-[#D7DEE5] font-mono-tick truncate">
                  {user.email}
                </div>
                {(() => {
                  const plan = (user?.plan || user?.subscriptionTier || "").toLowerCase();
                  const status = (user?.subscriptionStatus || "").toLowerCase();
                  const active = status === "active" || status === "trialing";
                  const isPro = active && plan.startsWith("professional_");
                  const isPrem = active && plan.startsWith("premium_");
                  if (isPro) {
                    return (
                      <>
                        <DropdownMenuItem
                          onSelect={() => navigate("/my/professional")}
                          className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold cursor-pointer px-3 py-2"
                          data-testid="header-my-professional"
                        >
                          <span className="inline-block w-4 mr-2 bg-[#0B1F33] text-white text-center text-[9px] font-bold leading-4">Prof</span>
                          Professional Feed
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => navigate("/my/premium")}
                          className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold cursor-pointer px-3 py-2"
                          data-testid="header-my-premium"
                        >
                          <span className="inline-block w-4 mr-2 bg-[#B8E9F1] text-[#0B1F33] text-center text-[10px] font-bold leading-4">P</span>
                          Premium Feed
                        </DropdownMenuItem>
                      </>
                    );
                  }
                  if (isPrem) {
                    return (
                      <DropdownMenuItem
                        onSelect={() => navigate("/my/premium")}
                        className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold cursor-pointer px-3 py-2"
                        data-testid="header-my-premium"
                      >
                        <span className="inline-block w-4 mr-2 bg-[#B8E9F1] text-[#0B1F33] text-center text-[10px] font-bold leading-4">P</span>
                        Premium Feed
                      </DropdownMenuItem>
                    );
                  }
                  return (
                    <DropdownMenuItem
                      onSelect={() => navigate("/subscribe")}
                      className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold cursor-pointer px-3 py-2"
                      data-testid="header-upgrade"
                    >
                      <Sparkles className="w-3 h-3 mr-2 text-[#0891B2]" /> Upgrade
                    </DropdownMenuItem>
                  );
                })()}
                <DropdownMenuItem
                  onSelect={signOut}
                  data-testid="header-signout"
                  className="rounded-none font-condensed uppercase tracking-[0.06em] text-[12px] font-semibold cursor-pointer px-3 py-2"
                >
                  <LogOut className="w-3 h-3 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={signIn}
              data-testid="header-signin"
              className="hidden md:inline-flex items-center border border-[#0B1F33] hover:bg-[#0B1F33] hover:text-white text-[#0B1F33] font-condensed uppercase tracking-[0.14em] text-[12px] font-bold px-4 py-2 transition-colors"
            >
              Sign In
            </button>
          )}
          <button
            onClick={() => setMobileOpen((m) => !m)}
            className="lg:hidden p-2 hover:bg-[#D7DEE5] rounded transition-colors"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5 text-[#0B1F33]" /> : <Menu className="w-5 h-5 text-[#0B1F33]" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — institutional pivot: single simple nav. */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#D7DEE5] bg-white" data-testid="mobile-nav">
          <div className="max-w-[1400px] mx-auto px-6 py-5 flex flex-col gap-4">
            <Link to="/" onClick={() => setMobileOpen(false)} className="font-condensed uppercase tracking-widest text-[13px] font-bold text-[#0B1F33]">Reports</Link>
            <Link to="/podcast" onClick={() => setMobileOpen(false)} className="font-condensed uppercase tracking-widest text-[13px] font-bold text-[#0B1F33]">Podcast</Link>
            <Link to="/masthead" onClick={() => setMobileOpen(false)} className="font-condensed uppercase tracking-widest text-[13px] font-bold text-[#0B1F33]">About</Link>
            <Link
              to="/subscribe"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] font-condensed uppercase tracking-[0.14em] text-[12px] font-bold px-4 py-2 transition-colors"
            >
              Subscribe
            </Link>
          </div>
        </div>
      )}

      {searchOpen && (
        <div className="border-t border-[#D7DEE5] bg-white">
          <form onSubmit={submit} className="max-w-[1400px] mx-auto px-6 py-4 flex gap-3">
            <Input autoFocus placeholder="Search research, markets, authors…" className="rounded-none border-[#D7DEE5] focus:border-[#B8E9F1] focus-visible:ring-0" />
            <Button type="submit" className="bg-[#0B1F33] hover:bg-[#132f57] rounded-none text-white uppercase tracking-wider text-[11px]">Search</Button>
          </form>
        </div>
      )}
    </header>
  );
};

export default Header;
